import { useQuery } from "react-query";
import addressService from "services/address";
import { useAuth } from "contexts/auth/auth.context";
import { useSettings } from "contexts/settings/settings.context";
import AddressList from "components/addressList/addressList";
import { useTranslation } from "react-i18next";
import cls from "./settings.module.scss";
import { IAddress } from "interfaces/address.interface";
import { useChangeAddress } from "hooks/useAddressServices";
import AsyncSelect from "components/asyncSelect/asyncSelect";
import GlobalLineIcon from "remixicon-react/GlobalLineIcon";
import nProgress from "nprogress";
import translationService from "services/translations";
import { setCookie } from "utils/session";
import { useContext } from "react";
import { ThemeContext } from "contexts/theme/theme.context";
import { useAppDispatch, useAppSelector } from "hooks/useRedux";
import { selectCurrency, setCurrency } from "redux/slices/currency";
import CoinLineIcon from "remixicon-react/CoinLineIcon";
import { useMediaQuery } from "@mui/material";
import useLocale from "hooks/useLocale";

export default function Settings() {
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAuth();
  const { updateAddress, updateLocation, updateLocationId } = useSettings();
  const currency = useAppSelector(selectCurrency);
  const { setDirection } = useContext(ThemeContext);
  const isMobile = useMediaQuery("(max-width: 640px)");
  const { addResourceBundle } = useLocale();

  const { mutate: changeServerActiveAddress } = useChangeAddress();

  const { data: serverAddresses } = useQuery({
    queryKey: ["addresses", isAuthenticated, user?.id],
    queryFn: () =>
      addressService.getAll({
        perPage: 100,
      }),
    enabled: isAuthenticated,
    staleTime: 0,
  });

  const activeAddress = serverAddresses?.find((item) => item?.active);

  const handleChangeAddress = (item: IAddress) => {
    if (!item?.id) return;
    changeServerActiveAddress(item?.id);
    updateAddress(item.address?.address);
    updateLocation(item.location.join(","));
    updateLocationId(item.id.toString());
  };

  const handleChangeLanguage = (item: any) => {
    const isRTL = item?.backward;
    const lang = item?.locale;
    i18n.changeLanguage(item.locale);
    nProgress.start();
    translationService
      .getAll({ lang })
      .then(({ data }) => addResourceBundle(lang, "translation", data))
      .finally(() => {
        setCookie("locale", lang);
        i18n.changeLanguage(lang);
        setDirection(isRTL ? "rtl" : "ltr");
        nProgress.done();
      });
  };

  const handleChangeCurrency = (item: any) => {
    dispatch(setCurrency(item));
    setCookie("currency_id", item?.id);
  };

  return (
    <div className={cls.wrapper}>
      <h1 className={cls.mainTitle}>{t("site.settings")}</h1>
      {isAuthenticated && (
        <section className={cls.container}>
          <h2 className={cls.title}>{t("saved.locations")}</h2>
          <div className={cls.body}>
            <AddressList
              data={serverAddresses || []}
              changeAddress={handleChangeAddress}
              selectedAddressId={activeAddress?.id}
            />
          </div>
        </section>
      )}
      <section className={cls.container}>
        <h2 className={cls.title}>{t("interface")}</h2>
        <div className={cls.body}>
          <AsyncSelect
            queryKey="/rest/languages/active"
            extractTitle={(data) => data?.locale?.toUpperCase()}
            extractKey={(data) => data?.locale}
            label={t("language")}
            icon={<GlobalLineIcon size={24} />}
            value={{
              locale: i18n.language,
            }}
            onChange={handleChangeLanguage}
            fullWidth={isMobile}
          />
          <AsyncSelect
            queryKey="/rest/currencies/active"
            extractTitle={(data) => `${data?.title} - (${data?.symbol})`}
            extractKey={(data) => data?.id}
            label={t("currency")}
            icon={<CoinLineIcon size={24} />}
            value={currency}
            onChange={handleChangeCurrency}
            fullWidth={isMobile}
          />
        </div>
      </section>
    </div>
  );
}
